import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listVideos, Video } from "../api";
import { formatBytes, relativeTime } from "../utils";

export default function AdminVideos() {
  const nav = useNavigate();
  const [videos, setVideos] = useState<Video[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [titleFilter, setTitleFilter] = useState<string>("");

  useEffect(() => {
    listVideos()
      .then(setVideos)
      .catch((e) => {
        if (String(e.message).includes("401")) nav("/admin/login");
        else setErr(e.message);
      });
  }, [nav]);

  // Unique titles present in the result set → populate the filter dropdown
  const titles = useMemo(() => {
    if (!videos) return [] as string[];
    const s = new Set<string>();
    for (const v of videos) if (v.link_title) s.add(v.link_title);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [videos]);

  const filtered = useMemo(() => {
    if (!videos) return null;
    if (!titleFilter) return videos;
    return videos.filter((v) => v.link_title === titleFilter);
  }, [videos, titleFilter]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Videos received</h2>
        {videos && (
          <span className="text-sm text-concrete">
            {filtered?.length} / {videos.length}
          </span>
        )}
      </div>

      {videos && videos.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-concrete">Filter by link:</label>
          <select
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            className="px-3 py-1.5 bg-white border border-stone rounded-lg text-sm focus:outline-none focus:border-charcoal"
          >
            <option value="">All ({videos.length})</option>
            {titles.map((t) => {
              const count = videos.filter((v) => v.link_title === t).length;
              return (
                <option key={t} value={t}>
                  {t} ({count})
                </option>
              );
            })}
          </select>
          {titleFilter && (
            <button
              onClick={() => setTitleFilter("")}
              className="text-xs text-concrete underline hover:text-charcoal"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {err && <div className="text-red-600">{err}</div>}
      {!videos && !err && <div className="text-concrete">Loading…</div>}

      {videos && videos.length === 0 && (
        <div className="text-concrete py-12 text-center bg-white rounded-xl">
          No videos yet. Create a link in the <b>New link</b> tab.
        </div>
      )}

      {filtered && filtered.length === 0 && videos && videos.length > 0 && (
        <div className="text-concrete py-8 text-center bg-white rounded-xl">
          No videos match <b>{titleFilter}</b>.
        </div>
      )}

      {filtered && filtered.length > 0 && (
        <ul className="divide-y divide-stone bg-white rounded-xl shadow-sm">
          {filtered.map((v) => (
            <li key={v.video_id}>
              <Link
                to={`/admin/videos/${v.video_id}`}
                className="block p-4 hover:bg-ivory transition"
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-base truncate">
                      {v.link_title || "Untitled"}
                    </div>
                    <div className="text-sm text-concrete mt-0.5">
                      {relativeTime(v.uploaded_at)}
                      {" · "}
                      <span className="font-mono text-xs">
                        {new Date(v.uploaded_at).toLocaleString()}
                      </span>
                      {" · "}
                      {formatBytes(v.size_bytes)}
                    </div>
                    {v.recorder_name && (
                      <div className="text-xs text-concrete/80 mt-0.5">
                        from {v.recorder_name}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-concrete uppercase tracking-wider">
                    {v.status}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
